<template>
  <div class="event-reimbursement-files">
    <grid
      ref="reimbursementGrid"
      :show-header="true"
      :list-url="listUrl"
      loading-message="Loading files ..."
      :add-checkmarks="true"
      :initial-filters="initialFilters"
      @links-loaded="gridLoadedLinks"
      @data-loaded="gridLoadedData"
      @filters-changed="gridChangedFilters"
    >
      <template #header>
        <div
          v-if="showDownloadAll"
          class="btn-group"
        >
          <button
            type="button"
            class="btn btn-sm btn-success"
            @click.prevent="downloadSelectedZip"
          >
            Download selected as ZIP
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success dropdown-toggle dropdown-toggle-split me-2"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <span class="visually-hidden">Toggle Dropdown</span>
          </button>
          <div class="dropdown-menu dropdown-menu-end">
            <a
              class="dropdown-item"
              href="#"
              @click.prevent="downloadSelectedZipNoFolders"
            >
              ZIP with no sub-folders
            </a>
          </div>
        </div>
        <a
          v-if="showDownloadReport"
          :href="reportUrl || undefined"
          class="btn btn-sm btn-success me-2"
        >
          Report (xlsx)
        </a>
        <a
          v-if="showDownload2023Report"
          :href="reportWg2023Url || undefined"
          class="btn btn-sm btn-success me-2"
        >
          Report 2023 (xlsx)
        </a>
        <a
          v-if="showPrint"
          href="#"
          class="btn btn-sm btn-success me-2"
          @click.prevent="onPrintButton"
        >
          Print
        </a>
      </template>
      <template #action-cell="participantProps">
        <a
          :href="participantProps.item.links.download"
          class="btn btn-sm btn-success"
        >
          Download (ZIP)
        </a>
        <a
          v-if="showPrint"
          href="#"
          class="btn btn-sm btn-success"
          @click.prevent="onPrintParticipantButton(participantProps)"
        >
          Print
        </a>
      </template>
      <template #additional-row="participantProps">
        <tr>
          <td />
          <td :colspan="columnCount">
            <div class="participant-file-list">
              <div v-if="participantProps.item.attributes.has_statement_of_costs">
                <a
                  href="#"
                  @click.prevent="showStatementOfCosts(participantProps.item)"
                >
                  Statement of costs
                </a>
                <template v-if="statementOfCostsPdfVisible(participantProps)">(<a :href="participantProps.item.links.download_statement_of_costs">Download PDF</a>)</template>
                <div
                  v-if="participantHasStatementOfCostsFiles(participantProps)"
                  class="mb-3"
                >
                  <div
                    v-for="file of statementOfCostsFilesForParticipant(participantProps)"
                    :key="`costs-items-files-${file.id}`"
                    class="ms-4"
                  >
                    <a :href="file.downloadUrl">{{ file.title }} ({{ file.name }}; {{ file.size }})</a>
                    <span
                      v-if="file.lateUpload"
                      class="text-muted small"
                    ><span class="glyphicons warning text-warning" />{{ file.lateUpload }}</span>
                  </div>
                </div>
              </div>
              <div
                v-for="file in filesForParticipant(participantProps)"
                :key="`reimbursement-files-${file.id}`"
              >
                <a :href="file.downloadUrl">{{ file.title }} ({{ file.name }}; {{ file.size }})</a>
                <span
                  v-if="file.lateUpload"
                  class="text-muted small"
                ><span class="glyphicons warning text-warning" />{{ file.lateUpload }}</span>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </grid>
    <teleport
      to="#vue-portal-target"
      append
    >
      <statement-of-costs-modal ref="statementOfCostsModal" />
      <convert-to-pdf-modal ref="convertToPdfModal" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef} from "vue"
import URI from "urijs"

import ConvertToPdfModal, {type PrintFile} from './convert-to-pdf-modal.vue'
import Grid from "./grid.vue"
import StatementOfCostsModal from "./statement-of-costs-modal.vue"
import type {GridID} from './types'

import {isNonEmptyString, isPresent} from "../js-utils"

interface ParticipantFile {
  id: string;
  title: string;
  name: string;
  size: string;
  lateUpload: string | null;
  downloadUrl: string;
}

interface JsonApiRelationshipData {
  id: string;
}

interface JsonApiRelationship {
  data?: JsonApiRelationshipData[];
}

interface ReimbursementParticipant {
  id: GridID;
  attributes: {
    has_statement_of_costs: boolean;
  };
  relationships: {
    files: JsonApiRelationship;
    statement_of_costs_files?: JsonApiRelationship;
  };
  links: {
    download: string;
    download_statement_of_costs?: string | null;
    statement_of_costs: string;
  };
}

interface ReimbursementIncludedFile {
  id: string;
  type: string;
  attributes: {
    file_title: string;
    file_name: string;
    file_size: string;
    late_upload: string | null;
  };
  links: {
    download: string;
  };
}

interface ReimbursementJsonApiData {
  included: ReimbursementIncludedFile[];
  meta?: {
    type_id?: number;
  };
}

interface ParticipantProps {
  item: ReimbursementParticipant;
}

type ReimbursementGrid = InstanceType<typeof Grid> & {
  columns: unknown[];
  reload: () => void;
  selectedItemIds: GridID[];
  setFilter: (filterName: string, value: string) => void;
}

defineProps({
  listUrl: {default: null, type: String},
  initialFilters: {default: null, type: String},
})

const reimbursementGrid = useTemplateRef<ReimbursementGrid>("reimbursementGrid")
const statementOfCostsModal = useTemplateRef<InstanceType<typeof StatementOfCostsModal>>("statementOfCostsModal")
const convertToPdfModal = useTemplateRef<InstanceType<typeof ConvertToPdfModal>>("convertToPdfModal")

const jsonApiData = ref<ReimbursementJsonApiData | null>(null)
const downloadUrl = ref<string | null>(null)
const downloadNoFoldersUrl = ref<string | null>(null)
const reportUrl = ref<string | null>(null)
const reportWg2023Url = ref<string | null>(null)
const printUrl = ref<string | null>(null)

const showDownloadAll = computed((): boolean => isNonEmptyString(downloadUrl.value))
const showDownloadReport = computed((): boolean => isNonEmptyString(reportUrl.value))
const showDownload2023Report = computed((): boolean => isNonEmptyString(reportWg2023Url.value))
const showPrint = computed((): boolean => isNonEmptyString(printUrl.value))
const columnCount = computed((): number => reimbursementGrid.value?.columns.length || 0)

const getLink = (value: unknown): string | null => {
  if (isNonEmptyString(value)) {
    return value
  }

  return null
}

const gridLoadedLinks = (links: Record<string, unknown>): void => {
  downloadUrl.value = getLink(links.download)
  downloadNoFoldersUrl.value = getLink(links.download_no_folders)
  reportUrl.value = getLink(links.report)
  reportWg2023Url.value = getLink(links.report_wg2023)
  printUrl.value = getLink(links.print)
}

const gridLoadedData = (data: ReimbursementJsonApiData): void => {
  jsonApiData.value = data
}

const gridChangedFilters = (filterParams: Record<string, unknown>): void => {
  const typeId = filterParams.type_id as string | number | null | undefined
  if (isPresent(typeId)) {
    history.pushState({type_id: typeId}, 'Reimbursement filter', `?type_id=${typeId}`)
  } else {
    history.pushState({type_id: typeId}, 'Reimbursement filter', '?type_id=0')
  }
}

const reimbursementFileFor = (fileRel: JsonApiRelationshipData): ReimbursementIncludedFile | undefined => {
  return jsonApiData.value?.included.find((item) => {
    return (item.type === 'reimbursement_files' || item.type === 'ospe-reimbursement-files') && item.id === fileRel.id
  })
}

const participantFileFor = (item: ReimbursementIncludedFile): ParticipantFile => {
  return {
    id: item.id,
    title: item.attributes.file_title,
    name: item.attributes.file_name,
    size: item.attributes.file_size,
    lateUpload: item.attributes.late_upload,
    downloadUrl: item.links.download
  }
}

const filesForParticipant = (participant: ParticipantProps): ParticipantFile[] => {
  const files = []
  for (const fileRel of participant.item.relationships.files.data || []) {
    const item = reimbursementFileFor(fileRel)
    if (item) {
      files.push(participantFileFor(item))
    }
  }
  return files
}

const participantHasStatementOfCostsFiles = (participant: ParticipantProps): boolean => {
  if (!participant.item.relationships.statement_of_costs_files) {
    return false
  }
  return (participant.item.relationships.statement_of_costs_files.data || []).length > 0
}

const statementOfCostsFilesForParticipant = (participant: ParticipantProps): ParticipantFile[] => {
  if (!participant.item.relationships.statement_of_costs_files?.data) {
    return []
  }

  const files = []
  for (const fileRel of participant.item.relationships.statement_of_costs_files.data) {
    const item = reimbursementFileFor(fileRel)
    if (item) {
      files.push(participantFileFor(item))
    }
  }
  return files
}

const selectedItemIds = (): GridID[] | undefined => reimbursementGrid.value?.selectedItemIds

const downloadZip = (url: string): void => {
  const itemIds = selectedItemIds()
  if (Array.isArray(itemIds) && itemIds.length > 0) {
    const uri = new URI(url)
    uri.search({
      participant_ids: itemIds.join(',')
    })
    window.location.href = uri.toString()
  } else {
    alert('Please select at least one participant!')
  }
}

const downloadSelectedZip = (): void => {
  if (downloadUrl.value) {
    downloadZip(downloadUrl.value)
  }
}

const downloadSelectedZipNoFolders = (): void => {
  if (downloadNoFoldersUrl.value) {
    downloadZip(downloadNoFoldersUrl.value)
  }
}

const showStatementOfCosts = (participant: ReimbursementParticipant): void => {
  statementOfCostsModal.value?.showModal(participant.links.statement_of_costs)
}

const statementOfCostsPdfVisible = (participantProps: ParticipantProps): boolean => {
  return isPresent(participantProps.item.links.download_statement_of_costs)
}

const onPrintButton = (): void => {
  const itemIds = selectedItemIds()
  if (Array.isArray(itemIds) && itemIds.length > 0) {
    if (isNonEmptyString(printUrl.value)) {
      convertToPdfModal.value?.showModal(printUrl.value, itemIds)
    }
  } else {
    alert('Please select at least one participant!')
  }
}

const onPrintParticipantButton = (participantData: ParticipantProps): void => {
  const files = [] as PrintFile[]
  if (statementOfCostsPdfVisible(participantData)) {
    files.push({id: 'statement-of-costs', name: 'Statement of costs.pdf', selected: false})
  }
  if (participantHasStatementOfCostsFiles(participantData)) {
    for (const file of statementOfCostsFilesForParticipant(participantData)) {
      files.push({id: file.id, name: `${file.title} (${file.name})`, selected: false})
    }
  }
  for (const file of filesForParticipant(participantData)) {
    files.push({id: file.id, name: `${file.title} (${file.name})`, selected: false})
  }

  if (isNonEmptyString(printUrl.value)) {
    convertToPdfModal.value?.showModal(printUrl.value, [participantData.item.id], files)
  }
}

onMounted(() => {
  window.addEventListener("popstate", (event) => {
    const state = event.state
    let filterApplied = false
    if (typeof state === 'object' && state !== null) {
      if (state.type_id && (state.type_id.toString() == '1' || state.type_id.toString() == '2')) {
        reimbursementGrid.value?.setFilter('type_id', state.type_id.toString())
        filterApplied = true
      }
    }
    if (!filterApplied) {
      // popstate was null
      reimbursementGrid.value?.setFilter('type_id', '')
    }
    reimbursementGrid.value?.reload()
  })
})
</script>
